/**
 * Teslyn Cloud's calls for share links, the processors' zips and the Safety section (roadmap step 7; R162, R165;
 * docs/specs/cloud/deliverables.md; 20261102190000_deliverables.sql). The database decides who may do what: staff
 * read the tables under their policies, clients only through my_share_links, share_link_open and
 * my_safety_documents; every change goes through a function that checks the role.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  clientHazardKmlName,
  safetyContentType,
  type SafetyDocumentKind,
  type ShareLinkProvider,
} from '@teslyn/domain';
import { fileSha256, plainPortalError } from '../portal/api.ts';

const fail = (e: { message: string; code?: string } | null, fallback: string): never => {
  throw new Error(e ? plainPortalError(e) : fallback);
};

// ---------------------------------------------------------------------------------------------- share links

export interface ShareLinkRow {
  readonly id: string;
  readonly project_id: string;
  readonly label: string;
  readonly url: string;
  readonly provider: ShareLinkProvider;
  readonly password_protected: boolean;
  readonly expires_on: string | null;
  readonly note: string;
  readonly shown: boolean;
  readonly shown_at: string | null;
  readonly shown_by_name: string;
  readonly withdrawn_at: string | null;
  readonly opened_count: number;
  readonly last_opened_at: string | null;
  readonly created_by_name: string;
  readonly created_at: string;
}

/** What a client sees of a link (never its address or the staff note). */
export interface ClientShareLink {
  readonly id: string;
  readonly project_id: string;
  readonly label: string;
  readonly provider: ShareLinkProvider;
  readonly password_protected: boolean;
  readonly expires_on: string | null;
  readonly shown_at: string;
}

export async function loadShareLinks(sb: SupabaseClient, projectId: string): Promise<ShareLinkRow[]> {
  const { data, error } = await sb
    .from('share_link')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) fail(error, 'The share links could not be read.');
  return (data ?? []) as ShareLinkRow[];
}

export async function loadClientShareLinks(
  sb: SupabaseClient,
  projectId: string,
): Promise<ClientShareLink[]> {
  const { data, error } = await sb.rpc('my_share_links', { p_project: projectId });
  if (error) fail(error, 'The share links could not be read.');
  return (Array.isArray(data) ? data : []) as ClientShareLink[];
}

export interface NewShareLink {
  readonly projectId: string;
  readonly label: string;
  readonly url: string;
  readonly provider: ShareLinkProvider;
  readonly passwordProtected: boolean;
  readonly expiresOn: string | null;
  readonly note: string;
}

export async function addShareLink(sb: SupabaseClient, l: NewShareLink): Promise<ShareLinkRow> {
  const { data, error } = await sb.rpc('share_link_add', {
    p_project: l.projectId,
    p_label: l.label,
    p_url: l.url,
    p_provider: l.provider,
    p_password: l.passwordProtected,
    p_expires_on: l.expiresOn,
    p_note: l.note,
  });
  if (error) fail(error, 'The link was not added.');
  return data as ShareLinkRow;
}

export async function setShareLinkShown(sb: SupabaseClient, id: string, shown: boolean): Promise<void> {
  const { error } = await sb.rpc('share_link_set_shown', { p_link: id, p_shown: shown });
  if (error) fail(error, 'The link was not changed.');
}

export async function withdrawShareLink(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.rpc('share_link_withdraw', { p_link: id });
  if (error) fail(error, 'The link was not withdrawn.');
}

/** The address, asked for when opened: checked, and for a client counted and audited (ADR-008). */
export async function openShareLink(sb: SupabaseClient, id: string): Promise<string> {
  const { data, error } = await sb.rpc('share_link_open', { p_link: id });
  if (error || typeof data !== 'string') fail(error, 'The link could not be opened.');
  return data as string;
}

// ---------------------------------------------------------------------------------------------- processors' zips

export interface DeliverableRow {
  readonly id: string;
  readonly project_id: string;
  readonly kind: 'processor_zip';
  readonly file_name: string;
  readonly destination: 'survera' | 'nextcloud';
  readonly path: string;
  readonly size_bytes: number;
  readonly sha256: string;
  readonly day: string;
  readonly jday: number | null;
  readonly flight_number: number | null;
  readonly rego: string;
  readonly report_revision: number | null;
  readonly contents: readonly { readonly name: string; readonly size_bytes: number }[];
  readonly replaced: readonly { readonly report_revision: number | null }[];
  readonly emailed: number;
  readonly uploaded_at: string;
  readonly uploaded_by_name: string;
}

export async function loadDeliverables(sb: SupabaseClient, projectId: string): Promise<DeliverableRow[]> {
  const { data, error } = await sb
    .from('deliverable')
    .select('*')
    .eq('project_id', projectId)
    .order('day', { ascending: false })
    .order('flight_number', { ascending: false });
  if (error) fail(error, 'The processors’ zips could not be read.');
  return (data ?? []) as DeliverableRow[];
}

/** A 5-minute download address of a zip kept in Teslyn Cloud storage. */
export async function deliverableUrl(sb: SupabaseClient, d: DeliverableRow): Promise<string> {
  const { data, error } = await sb.storage
    .from('deliverables')
    .createSignedUrl(d.path, 300, { download: d.file_name });
  if (error || !data) fail(error, 'The zip could not be opened.');
  return data!.signedUrl;
}

// ---------------------------------------------------------------------------------------------- Safety

export interface SafetyDocumentRow {
  readonly id: string;
  readonly project_id: string;
  readonly kind: SafetyDocumentKind;
  readonly version: number;
  readonly file_name: string;
  readonly storage_path: string | null;
  readonly size_bytes: number | null;
  readonly source: 'upload' | 'signoff' | 'hazard_pack';
  readonly signoff_request_id: string | null;
  readonly shown: boolean;
  readonly shown_at: string | null;
  readonly shown_by_name: string;
  readonly added_by_name: string;
  readonly created_at: string;
  readonly removed_at: string | null;
}

/** What a client sees under Safety: per kind, the version shown. */
export interface ClientSafetyDocument {
  readonly id: string;
  readonly kind: SafetyDocumentKind;
  readonly version: number;
  readonly file_name: string;
  readonly storage_path: string | null;
  readonly size_bytes: number | null;
  readonly shown_at: string;
  readonly pack_version: number | null;
  readonly pack_published_at: string | null;
}

export async function loadSafetyDocuments(
  sb: SupabaseClient,
  projectId: string,
): Promise<SafetyDocumentRow[]> {
  const { data, error } = await sb
    .from('safety_document')
    .select('*')
    .eq('project_id', projectId)
    .order('kind')
    .order('version', { ascending: false });
  if (error) fail(error, 'The Safety documents could not be read.');
  return (data ?? []) as SafetyDocumentRow[];
}

export async function loadClientSafety(
  sb: SupabaseClient,
  projectId: string,
): Promise<ClientSafetyDocument[]> {
  const { data, error } = await sb.rpc('my_safety_documents', { p_project: projectId });
  if (error) fail(error, 'The Safety documents could not be read.');
  return (Array.isArray(data) ? data : []) as ClientSafetyDocument[];
}

/** The pilot hazard pack of the project (staff), to say whether the hazards KML has anything to give. */
export async function loadHazardPack(
  sb: SupabaseClient,
  projectId: string,
): Promise<{ readonly version: number; readonly published_at: string } | null> {
  const { data, error } = await sb
    .from('hazard_pack')
    .select('version, published_at')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) fail(error, 'The hazard pack could not be read.');
  return (data ?? null) as { version: number; published_at: string } | null;
}

/** Signed sign-off requests of the project's JSA and JSP (the DocuSeal copies staff can add). */
export interface SignedCopy {
  readonly id: string;
  readonly document_kind: 'jsa' | 'jsp_meeting';
  readonly title: string;
  readonly document_revision: number;
  readonly completed_at: string | null;
}

export async function loadSignedCopies(sb: SupabaseClient, projectId: string): Promise<SignedCopy[]> {
  const { data, error } = await sb
    .from('signoff_request')
    .select('id, document_kind, title, document_revision, completed_at')
    .eq('project_id', projectId)
    .eq('state', 'completed')
    .eq('signed_available', true)
    .is('deleted_at', null)
    .order('completed_at', { ascending: false });
  if (error) fail(error, 'The signed copies could not be read.');
  return (data ?? []) as SignedCopy[];
}

/**
 * Add a version: the file goes to the `safety` bucket at its document's own path, then the database records it (and
 * shows it in place of an older version shown to the client).
 */
export async function addSafetyDocument(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
  kind: Exclude<SafetyDocumentKind, 'hazards_kml'>,
  file: Blob,
  fileName: string,
  signoffRequestId: string | null = null,
  newId: () => string = () => crypto.randomUUID(),
): Promise<SafetyDocumentRow> {
  const id = newId();
  const path = `tenants/${project.tenantId}/projects/${project.id}/safety/${id}.${
    kind === 'risk_assessment' ? 'xlsm' : 'pdf'
  }`;
  const sha = await fileSha256(file);
  if (!sha) throw new Error('This browser could not read the file. Try again, or use another browser.');
  const up = await sb.storage
    .from('safety')
    .upload(path, file, { contentType: safetyContentType(fileName), upsert: false });
  if (up.error) fail(up.error, 'The file did not reach Teslyn Cloud.');
  const { data, error } = await sb.rpc('safety_document_add', {
    p_id: id,
    p_project: project.id,
    p_kind: kind,
    p_file_name: fileName,
    p_size: file.size,
    p_sha256: sha,
    p_signoff: signoffRequestId,
  });
  if (error) {
    await sb.storage.from('safety').remove([path]);
    fail(error, 'The document was not added.');
  }
  return data as SafetyDocumentRow;
}

export async function setSafetyShown(sb: SupabaseClient, id: string, shown: boolean): Promise<void> {
  const { error } = await sb.rpc('safety_document_set_shown', { p_id: id, p_shown: shown });
  if (error) fail(error, 'The document was not changed.');
}

export async function setHazardsShown(sb: SupabaseClient, projectId: string, shown: boolean): Promise<void> {
  const { error } = await sb.rpc('safety_hazards_set_shown', { p_project: projectId, p_shown: shown });
  if (error) fail(error, 'The hazards KML was not changed.');
}

export async function removeSafetyDocument(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.rpc('safety_document_remove', { p_id: id });
  if (error) fail(error, 'The document was not removed.');
}

/** A 5-minute download address of a version (the storage policy decides who may). */
export async function safetyFileUrl(
  sb: SupabaseClient,
  d: { readonly storage_path: string | null; readonly file_name: string },
): Promise<string> {
  if (!d.storage_path) throw new Error('This document has no file.');
  const { data, error } = await sb.storage
    .from('safety')
    .createSignedUrl(d.storage_path, 300, { download: d.file_name });
  if (error || !data) fail(error, 'The document could not be opened.');
  return data!.signedUrl;
}

/** The hazards KML as the client may have it (portal-hazards { as: 'kml' }). */
export async function hazardsKml(
  sb: SupabaseClient,
  projectId: string,
  projectNumber: string,
): Promise<{ readonly fileName: string; readonly kml: string }> {
  const { data, error } = await sb.functions.invoke<{ file_name?: string; kml?: string; error?: string }>(
    'portal-hazards',
    { body: { project_id: projectId, as: 'kml' } },
  );
  if (error || !data?.kml) {
    const context = (error as { context?: Response } | null)?.context;
    const body =
      context && typeof context.json === 'function' ? await context.json().catch(() => null) : null;
    const w = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
    throw new Error(typeof w === 'string' && w ? w : 'The hazards KML could not be made. Try again shortly.');
  }
  return { fileName: data.file_name ?? clientHazardKmlName(projectNumber), kml: data.kml };
}
